/* eslint-disable no-cond-assign */
/* eslint-disable class-methods-use-this */
/* eslint-disable prefer-destructuring */

import moment from 'moment';
import round from 'lodash/round';

import { GenericCell, NotApplicableCell, TextCell, PercentageCell } from '../../../../../views/components/cells';
import * as ProposalNS from '../../../Proposal';
import * as ProposalElementNS from '../../../ProposalElements';
import { ProposalStyleClassNames } from '../../../ProposalStyleClassNames';
import { DisclaimerConfig, ProposalDAOInterface } from '../../ProposalDAO';
import { BMOGIFBasedProposalDAO } from '../../ProposalDAOImpl';
import { ProposalFieldDAO } from '../../ProposalFieldDAO';
import { BMOGIFItemDAO } from '../../ProposalItemDAO';
import { BMOi18n, GIF_CLASS_TYPES, GIF_PLAN_OPTIONS, GIF_PORTFOLIO_TYPES, SALES_CHARGES } from '../../../../../utils/bmo-gif/constants';
import { formatCurrency } from '../../../../../utils/i18n';
import { getKeyByValue } from '../../../../../utils/objects';
import { ACCOUNT_TYPES } from '../../../../../utils/constants';
import { isPrestige } from '../../../../../utils/bmo-gif/utils';
import { calculateAnnualizedNetReturn, getMaturityDate } from '../../../../../utils/bmo-gif/calculate';
import HTMLCell from '../../../../../views/components/cells/HTMLCell';
import { PROPOSAL_DISCLAIMERS } from '../../../utils/constants';
import LocalizedString from '@/types/localized-string';
import BMOGIFPortfolio from '@/types/bmo-gif/bmo-gif-portfolio';
import BMOContact from '@/types/bmo-gif/bmo-contact';

export default class BmoGifSummaryDAO extends BMOGIFBasedProposalDAO {
  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new BmoGifSummaryDAO(proposal, proposalElement);
  }

  isPrestige: boolean = false;

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return [];
  }

  protected build(): void {
    let section = 'ALL';

    const daoConfig: ProposalNS.BmoGifSummaryConfig = this.proposalElement.DAO as ProposalNS.BmoGifSummaryConfig;
    if (daoConfig !== undefined) {
      section = daoConfig.section;
    }

    const dateFormat = 'DD-MMM-YY';

    let itemDAO: BMOGIFItemDAO | null = null;
    while ((itemDAO = this.nextItem()) !== null) {
      const { portfolio } = itemDAO.item;
      if (isPrestige(portfolio.classType)) {
        this.isPrestige = true;
      }

      if (section === 'ALL' || section === 'PERSONAL_DATA') {
        // Contact name
        const name: string | undefined = this.proposal.contact !== undefined ? this.proposal.contact.fullName : '';
        this.addItemField(itemDAO, BMOi18n[this.locale].PROPOSAL.HEADERS.ANNUITANT_NAME, name, TextCell);

        // Contact DOB
        const dob: Date | undefined = this.proposal.contact !== undefined ? this.proposal.contact.dob : undefined;
        this.addItemField(itemDAO, BMOi18n[this.locale].PROPOSAL.HEADERS.DOB, this.formatDate(dob, dateFormat), TextCell);

        // Contact age
        let age: number | undefined;
        if (dob !== undefined) {
          age = this.getAge(dob, undefined, portfolio.purchaseDate);
        }
        this.addItemField(itemDAO, BMOi18n[this.locale].PROPOSAL.HEADERS.AGE, age, TextCell);

        const { contact } = this.proposal;
        if (contact && (contact.fullNamePolicyOwner || contact.fullNameSecondPolicyOwner)) {
          const { fullNamePolicyOwner, fullNameSecondPolicyOwner } = contact;
          const policyOwners: string = `
            <div class="summary-policy-owners">
              <span>${fullNamePolicyOwner}</span>
              <span>${fullNameSecondPolicyOwner}</span>
            </div>
          `;

          this.addItemField(itemDAO, BMOi18n[this.locale].PROPOSAL.HEADERS.POLICYOWNERS, policyOwners, HTMLCell);
        }
      }

      if (section === 'ALL' || section === 'INITIAL_DEPOSIT') {
        // Portfolio Amount
        const amount: string = formatCurrency(portfolio.depositAmount || 0, 0, undefined, false, this.locale);
        this.addItemField(itemDAO, BMOi18n[this.locale].PROPOSAL.HEADERS.AMOUNT, amount, GenericCell);

        // Portfolio Purchace Date
        const purchase: Date = portfolio.purchaseDate;
        this.addItemField(itemDAO, BMOi18n[this.locale].PROPOSAL.HEADERS.PURCHASE_DATE, this.formatDate(purchase, dateFormat), TextCell);
      }

      if (section === 'ALL' || section === 'INVESTMENT_ASSUMPTIONS') {
        // Guarantee Level
        const key = getKeyByValue(GIF_PLAN_OPTIONS, portfolio.planOption);
        const level: string = !!key && BMOi18n[this.locale].PROPOSAL[key];
        this.addItemField(itemDAO, BMOi18n[this.locale].PROPOSAL.HEADERS.GUARANTEE_LEVEL, level, TextCell);

        const type: string = this.getAccountType();
        this.addItemField(itemDAO, BMOi18n[this.locale].PROPOSAL.HEADERS.ACCOUNT_TYPE, type, TextCell);

        const classType: string = this.getClassTypeValue(portfolio);
        this.addItemField(itemDAO, BMOi18n[this.locale].PROPOSAL.HEADERS.CLASS, classType, TextCell);

        const funds: string = this.getFunds(portfolio);
        this.addItemField(itemDAO, BMOi18n[this.locale].PROPOSAL.HEADERS.FUNDS, funds, HTMLCell);

        const mer: string = `${round(portfolio.mer, 2)}%`;
        this.addItemField(itemDAO, BMOi18n[this.locale].PROPOSAL.HEADERS.MER, mer, TextCell);

        const ror: string = `${round(calculateAnnualizedNetReturn(itemDAO.item.baseScenario.data), 2)}%`;
        this.addItemField(itemDAO, BMOi18n[this.locale].PROPOSAL.HEADERS.ROR, ror, TextCell);

        const is100100 = portfolio.planOption === GIF_PLAN_OPTIONS.GIF100100;
        if (is100100 && portfolio.deathGuaranteeReset) {
          this.addItemField(itemDAO, BMOi18n[this.locale].PROPOSAL.HEADERS.DGRO, portfolio.deathBenefitFee, PercentageCell);
        }

        const term: string = `${portfolio.term} ${BMOi18n[this.locale].AGE_AND_YEARS.YEARS}`;
        this.addItemField(itemDAO, BMOi18n[this.locale].PROPOSAL.HEADERS.TERM, term, TextCell);

        const maturity: Date | undefined = portfolio.term !== undefined ? getMaturityDate(portfolio.term, new Date(portfolio.purchaseDate)) : undefined;
        this.addItemField(itemDAO, BMOi18n[this.locale].PROPOSAL.HEADERS.MATURITY, this.formatDate(maturity, dateFormat), TextCell);

        let dgr: string = portfolio.deathGuaranteeReset ? this.formatMessage('Common.Yes') : this.formatMessage('Common.No');
        const isDgrEnhanced = portfolio.deathGuaranteeReset && portfolio.deathGuaranteeResetEnhanced;
        dgr += isDgrEnhanced ? BMOi18n[this.locale].PROPOSAL.DGR_ENHANCED : '';
        this.addItemField(itemDAO, BMOi18n[this.locale].PROPOSAL.HEADERS.DEATH_BENEFIT_RESET, dgr, TextCell);

        const salesCharge: string = this.getSalesCharge(portfolio);
        this.addItemField(itemDAO, BMOi18n[this.locale].PROPOSAL.HEADERS.SALES_CHARGE, salesCharge, TextCell);
      }
    }
  }

  getDisclaimerConfigs(): Array<DisclaimerConfig> {
    const texts: LocalizedString[] = [
      {
        'en-CA': BMOi18n[this.locale].PROPOSAL.ROR_DISCLAIMER,
        'fr-CA': BMOi18n[this.locale].PROPOSAL.ROR_DISCLAIMER,
      },
    ];

    if (this.isPrestige) {
      texts.push({
        'en-CA': BMOi18n[this.locale].PROPOSAL.PRESTIGE_DISCLAIMER,
        'fr-CA': BMOi18n[this.locale].PROPOSAL.PRESTIGE_DISCLAIMER,
      });
    }

    return texts.map(text => ({
      key: PROPOSAL_DISCLAIMERS.CUSTOM_DISCLAIMER,
      config: { customString: text },
      items: [],
    }));
  }

  private getFunds(portfolio: BMOGIFPortfolio) {
    const first5Holdings = portfolio.holdings.slice(0, 5);
    const otherFunds = portfolio.holdings.slice(5, portfolio.holdings.length);
    const othersFundsWeight = otherFunds.reduce((acc, cur) => acc + cur.weight, 0);

    let out = '';

    if (portfolio.portfolioType === GIF_PORTFOLIO_TYPES.CUSTOM) {
      out += `<div>${BMOi18n[this.locale].CUSTOM}</div>`;
    }

    first5Holdings.forEach(holding => {
      out += `<div>${round(holding.weight, 2)}% ${this.formatLocalizedString(holding.name)}</div>`;
    });

    if (otherFunds !== undefined && otherFunds.length > 0) {
      out += `<div>${round(othersFundsWeight, 2)}% ${BMOi18n[this.locale].PROPOSAL.OTHERS}</div>`;
    }

    return `
      <div class="bmo-gif-funds">
        ${out}
      </div>
    `;
  }

  private getSalesCharge(portfolio: BMOGIFPortfolio) {
    const key = getKeyByValue(SALES_CHARGES, portfolio.salesChargeType);

    if (portfolio.salesChargeType === SALES_CHARGES.FEL && !!key && portfolio.salesChargeAmount !== undefined) {
      return `${BMOi18n[this.locale].PROPOSAL[key]} ${portfolio.salesChargeAmount.toFixed(2)}% `;
    }

    return !!key && BMOi18n[this.locale].PROPOSAL[key];
  }

  private getClassTypeValue(portfolio: BMOGIFPortfolio) {
    const key = getKeyByValue(GIF_CLASS_TYPES, portfolio.classType);

    return !!key && BMOi18n[this.locale].PROPOSAL[key];
  }

  private getAccountType() {
    const bmoContact = this.proposal.contact as BMOContact;

    if (bmoContact && bmoContact.registeredAccountType) {
      return BMOi18n[this.locale][bmoContact.registeredAccountType];
    }

    if (bmoContact.accountType === ACCOUNT_TYPES.TFSA || bmoContact.accountType === ACCOUNT_TYPES.NON_REGISTERED) {
      const key = getKeyByValue(ACCOUNT_TYPES, bmoContact.accountType);
      return this.formatMessage(`AccountTypes.${key}`);
    }

    return '';
  }

  private getAge(date: Date | string, format?: string, referenceDate?: Date) {
    const reference = referenceDate ? new Date(referenceDate) : new Date();
    const birthDate = moment(date, format).toDate();

    let age = reference.getFullYear() - birthDate.getFullYear();
    const monthDiff = reference.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && reference.getDate() < birthDate.getDate())) {
      age--;
    }

    return age > 0 ? age : undefined;
  }

  private addItemField(itemDAO: BMOGIFItemDAO, name: string, value: any, cellType: any): void {
    const field: ProposalFieldDAO = new ProposalFieldDAO(name, value != null ? value : '', ProposalStyleClassNames.ProposalTableContent);
    field.cellType = value != null ? cellType : NotApplicableCell;
    this.addFieldDAO(itemDAO, field);
    this.addFieldName(name, ProposalStyleClassNames.ProposalTableContent);
  }
}
